import ViewportTransform from './viewport-transform';
import { recognizeGestures } from './gesture-recognizer';
import ViewportPhysics from './viewport-physics';
import LazyImage from './lazy-image';
import { FALLBACK_IMAGE_ID, getImageUrl } from '../../_common/client/images';
import { getCurrentMajorBreakpoint, MajorBreakpoint } from '../../_common/client/breakpoint';

type Config = {
    imageId: string;
    imageHeight: number;
    imageWidth: number;
    imageAlt?: string;
    imageClassTokens: string[];
};

export class ZoomImageElement extends HTMLElement {
    public static readonly EVENT_OPENED = 'pdp-zoom-image-opened';
    public static readonly EVENT_CLOSED = 'pdp-zoom-image-closed';
    public static readonly EVENT_CLOSE = 'ft5.pdp.close-zoom-image';

    private static readonly TPS = 60;
    private static readonly TIMESTEP = 1000 / this.TPS;
    private static readonly CLIP_CLOSE_DELAY = 400;
    private static readonly IMAGE_MAX_CLIP = 0.5;
    private static readonly IMAGE_MAX_OFFSET = 0.5;

    private static readonly STYLE_ID = 'pdp_zoom-image-styles';
    private static readonly STYLES = `
        @keyframes pdp_zoom-image__curtain-open {
            from { background-color: #00000000; }
            to {}
        }

        @keyframes ani_pdp_zoom-image__fixed-image--closing {
            from {}
            to { transform: translate3d(0, 0, 0) scale(1); }
        }

        .pdp_zoom-image {
            touch-action: pan-x pan-y;
            line-height: 0;
            display: inline-block;
            height: 100%;
            text-align: center;
            user-select: none;
            position: relative;
        }

        .pdp_zoom-image__fixed-image {
            --height: auto;
            --width: auto;
            --top: 0;
            --left: 0;
            --image-transform: translate3d(0, 0, 0) scale(1);
            position: fixed;
            height: var(--height);
            width: var(--width);
            left: var(--left);
            top: var(--top);
            transform-origin: 0 0 0;
            transform: var(--image-transform);
            z-index: 9100;
        }

        .pdp_zoom-image__fixed-image--closing {
            animation: ani_pdp_zoom-image__fixed-image--closing 450ms;
        }

        .pdp_zoom-image__curtain {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 9099;
            background-color: #0000007f;
            transition: background-color 400ms;
            animation: pdp_zoom-image__curtain-open 400ms;
        }

        .pdp_zoom-image__curtain--closing {
            background-color: #00000000;
        }

        .pdp_zoom-image--active {
            touch-action: none;
        }

        .pdp_zoom-image__content {
            display: inline-block;
            max-height: 100%;
            max-width: 100%;
        }

        .pdp_zoom-image--active .pdp_zoom-image__content {
            visibility: hidden;
        }

        html.pdp_zoom-image__no-scrolling {
            overflow: hidden;
            touch-action: none;
        }
    `;

    private static active = false;
    private isActiveInstance = false;
    private config: Config = {
        imageId: FALLBACK_IMAGE_ID,
        imageHeight: 100,
        imageWidth: 100,
        imageClassTokens: [],
    };

    private contentElement = document.createElement('img');
    private fixedImageElement = document.createElement('img');
    private curtainElement = document.createElement('div');

    private readonly viewportTransform: ViewportTransform;
    private readonly viewportPhysics: ViewportPhysics;
    private lazyImage: LazyImage | null = null;

    private dragging = false;
    private shouldCloseOnTouchEnd = false;
    private previousTouches: TouchList | null = null;
    private scrolling = false;

    private lastOpenTime = 0;
    private tickIntervalId = 0;

    private currentCssTransform?: string;

    private globalEventListeners: Map<string, (evt: Event) => void> = new Map();
    private closeEventListener: (() => void) | null = null;

    constructor() {
        super();
        this.viewportTransform = new ViewportTransform();
        this.viewportPhysics = new ViewportPhysics(this.viewportTransform);
    }

    /*                                                        */
    public connectedCallback() {
        if (!this.isConnected) return;
        ZoomImageElement.injectStyles();
        if (getCurrentMajorBreakpoint() === MajorBreakpoint.Desktop) return;
        this.initializeConfig();
        this.build();
        this.setEventListeners();
        this.registerLazyLoader();
    }

    /*                                                             */
    public disconnectedCallback() {
        this.removeGlobalEventListeners();
        this.stopTickLoop();
    }

    private static injectStyles() {
        if (document.getElementById(ZoomImageElement.STYLE_ID)) return;
        const style = document.createElement('style');
        style.id = ZoomImageElement.STYLE_ID;
        style.textContent = ZoomImageElement.STYLES;
        document.head.appendChild(style);
    }

    private initializeConfig() {
        const { imageId, imageAlt, imageHeight, imageWidth, imageClass } = this.dataset;

        this.config.imageId = imageId || this.config.imageId;
        this.config.imageHeight = this.parseImageDimension(imageHeight) || this.config.imageHeight;
        this.config.imageWidth = this.parseImageDimension(imageWidth) || this.config.imageWidth;
        this.config.imageAlt = imageAlt;
        this.config.imageClassTokens = imageClass?.trim() ? imageClass.trim().split(/[ \n\t]+/) : [];
    }

    private parseImageDimension(imageDimension?: string): number | undefined {
        const parsedImageDimension = parseInt(imageDimension || '', 10);
        return isNaN(parsedImageDimension) ? undefined : parsedImageDimension;
    }

    private registerLazyLoader() {
        const url = getImageUrl(this.config.imageId, '?h=3040&w=3040&sm=clamp&upscale=true&fmt=auto');
        this.lazyImage = new LazyImage(this.fixedImageElement, url);
    }

    private startTickLoop() {
        this.tickIntervalId = window.setInterval(() => this.tick(), ZoomImageElement.TIMESTEP);
    }

    private stopTickLoop() {
        window.clearInterval(this.tickIntervalId);
    }

    private tick() {
        this.viewportPhysics.tick(ZoomImageElement.TIMESTEP);
        this.updateTransform();
    }

    public build() {
        this.classList.add('pdp_zoom-image');

        const imgElement = this.querySelector('img');
        if (imgElement) {
            imgElement.classList.add('pdp_zoom-image__content');
            this.contentElement = imgElement;
        } else {
            this.contentElement = this.createImageElement();
            this.appendChild(this.contentElement);
        }

        this.curtainElement.className = 'pdp_zoom-image__curtain';

        this.fixedImageElement.className = 'pdp_zoom-image__fixed-image';
        this.fixedImageElement.addEventListener('animationend', evt => this.onAnimationEnd(evt));
    }

    private createImageElement() {
        const { imageId, imageHeight, imageWidth, imageAlt, imageClassTokens } = this.config;
        const imageElement = document.createElement('img');
        imageElement.className = 'pdp_zoom-image__content js_pdp_zoom-image__content';
        imageElement.loading = 'lazy';
        imageElement.src = getImageUrl(imageId, `h=${imageHeight}&w=${imageWidth}&sm=clamp&upscale=true&fmt=auto`);
        imageElement.srcset =
            getImageUrl(
                imageId,
                `h=${imageHeight * 2}&w=${imageWidth * 2}&qlt=40&fmt.options=interlaced&unsharp=0,1,0.6,7&sm=clamp&upscale=true&fmt=auto`
            ) + ' 2x';
        if (imageAlt) imageElement.alt = imageAlt;
        imageElement.classList.add(...imageClassTokens);
        imageElement.onerror = () => {
            imageElement.src = imageElement.src.replace(imageId, FALLBACK_IMAGE_ID);
            imageElement.srcset = imageElement.srcset.replace(imageId, FALLBACK_IMAGE_ID);
            imageElement.alt = 'ohne Abbildung';
            imageElement.onerror = null;
        };

        return imageElement;
    }

    private setEventListeners() {
        this.closeEventListener = () => this.toggleActive(false);
        o_global.eventQBus.on(ZoomImageElement.EVENT_CLOSE, this.closeEventListener);
        this.addGlobalEventListener('resize', () => this.toggleActive(false));

        if (window.TouchEvent) {
            this.addGlobalEventListener('touchstart', (evt: TouchEvent) => this.onWindowTouchStart(evt));
            this.addGlobalEventListener('touchmove', (evt: TouchEvent) => this.onWindowTouchMove(evt));
            this.addGlobalEventListener('touchend', (evt: TouchEvent) => this.onWindowTouchEnd(evt));
        }
    }

    private addGlobalEventListener<E extends Event>(eventName: string, listener: (evt: E) => void) {
        if (this.globalEventListeners.has(eventName)) throw new Error(`Global event listener for ${eventName} is already bound!`);
        this.globalEventListeners.set(eventName, listener as (evt: Event) => void);
        window.addEventListener(eventName, listener as (evt: Event) => void);
    }

    private removeGlobalEventListeners() {
        if (this.closeEventListener) o_global.eventQBus.off(ZoomImageElement.EVENT_CLOSE, this.closeEventListener);
        for (const [evtName, listener] of this.globalEventListeners.entries()) {
            window.removeEventListener(evtName, listener);
        }
    }

    private onWindowTouchStart(evt: TouchEvent) {
        if (evt.target === this.contentElement || this.isActiveInstance) {
            if (this.scrolling) return;
            if (this.isActiveInstance || evt.touches.length >= 2) {
                this.toggleActive(true);
                this.startDrag();
                this.previousTouches = evt.touches;
                this.shouldCloseOnTouchEnd = true;
            }
        }
    }

    private onWindowTouchEnd(evt: TouchEvent) {
        if (evt.touches.length == 0) this.scrolling = false;
        if (this.isActiveInstance && this.dragging) {
            this.previousTouches = evt.touches;
            if (evt.touches.length == 0) {
                this.endDrag(true);
            }
        }
        if (this.isActiveInstance && this.shouldCloseOnTouchEnd) {
            this.toggleActive(false);
        }
    }

    private onWindowTouchMove(evt: TouchEvent) {
        if (!this.isActiveInstance) {
            this.previousTouches = null;
            this.scrolling = true;
            return;
        }
        if (this.dragging) {
            this.applyGesturesFromTouchMoveEvent(evt);
        }
        this.shouldCloseOnTouchEnd = false;
    }

    private onAnimationEnd(evt: AnimationEvent) {
        if (evt.animationName === 'ani_pdp_zoom-image__fixed-image--closing') {
            this.classList.remove('pdp_zoom-image--active');
            document.body.removeChild(this.fixedImageElement);
            document.body.removeChild(this.curtainElement);
            this.fixedImageElement.classList.remove('pdp_zoom-image__fixed-image--closing');
            this.curtainElement.classList.remove('pdp_zoom-image__curtain--closing');
            document.documentElement.classList.remove('pdp_zoom-image__no-scrolling');
            this.currentCssTransform = '';
        }
    }

    private startDrag() {
        this.dragging = true;
        this.viewportPhysics.setEnabled(false);
        this.viewportPhysics.resetTimer();
    }

    private endDrag(smooth: boolean) {
        this.dragging = false;
        if (smooth) this.viewportPhysics.setEnabled(true);
    }

    private applyGesturesFromTouchMoveEvent(evt: TouchEvent) {
        if (!this.previousTouches) return;
        const gestures = recognizeGestures(this.fixedImageElement, evt.touches, this.previousTouches);

        if (this.viewportTransform.zoom * gestures.zoom < 1) {
            this.toggleActive(false);
            return;
        }

        this.viewportPhysics.moveBy(gestures.translation);
        this.viewportTransform.zoomBy(gestures.zoom, gestures.zoomCenter);
        this.updateTransform();
        this.previousTouches = evt.touches;
    }

    private contentIsOffscreen(): boolean {
        return this.contentIsOverMaxClipAmount() && this.contentIsOverMaxOffset();
    }

    private contentIsOverMaxClipAmount() {
        const clientRect = this.fixedImageElement.getBoundingClientRect();
        const clipLeft = Math.max(-clientRect.x, 0);
        const clipRight = Math.max(clientRect.x + clientRect.width - window.innerWidth, 0);
        const clipTop = Math.max(-clientRect.y, 0);
        const clipBottom = Math.max(clientRect.y + clientRect.height - window.innerHeight, 0);

        const maxClipX = ZoomImageElement.IMAGE_MAX_CLIP * clientRect.width;
        const maxClipY = ZoomImageElement.IMAGE_MAX_CLIP * clientRect.height;

        return clipLeft > maxClipX || clipRight > maxClipX || clipTop > maxClipY || clipBottom > maxClipY;
    }

    private contentIsOverMaxOffset() {
        const clientRect = this.fixedImageElement.getBoundingClientRect();
        const offsLeft = Math.max(clientRect.x, 0);
        const offsRight = Math.max(window.innerWidth - clientRect.x - clientRect.width, 0);
        const offsTop = Math.max(clientRect.y, 0);
        const offsBottom = Math.max(window.innerHeight - clientRect.y - clientRect.height, 0);

        const maxOffsX = ZoomImageElement.IMAGE_MAX_OFFSET * window.innerWidth;
        const maxOffsY = ZoomImageElement.IMAGE_MAX_OFFSET * window.innerHeight;

        return offsLeft > maxOffsX || offsRight > maxOffsX || offsTop > maxOffsY || offsBottom > maxOffsY;
    }

    private updateTransform() {
        const futureCssTransform = this.viewportTransform.createCSSTransform();
        if (futureCssTransform !== this.currentCssTransform) {
            this.fixedImageElement.style.setProperty('--image-transform', futureCssTransform);
            this.currentCssTransform = futureCssTransform;
        }
        const timeSinceOpen = Date.now() - this.lastOpenTime;
        if (this.isActiveInstance && timeSinceOpen > ZoomImageElement.CLIP_CLOSE_DELAY && this.contentIsOffscreen()) {
            this.toggleActive(false);
        }
    }

    private toggleActive(active: boolean) {
        if (ZoomImageElement.active == active) return;
        ZoomImageElement.active = active;
        this.isActiveInstance = active;
        if (active) {
            this.startTickLoop();
            const rect = this.contentElement.getBoundingClientRect();
            this.fixedImageElement.src = this.contentElement.currentSrc;
            this.fixedImageElement.style.height = `${this.contentElement.offsetHeight}px`;
            this.fixedImageElement.style.width = `${this.contentElement.offsetWidth}px`;
            this.fixedImageElement.style.top = `${rect.top}px`;
            this.fixedImageElement.style.left = `${rect.left}px`;

            document.body.appendChild(this.curtainElement);
            document.body.appendChild(this.fixedImageElement);

            this.lazyImage?.reset();
            this.lazyImage?.load();
            document.documentElement.classList.add('pdp_zoom-image__no-scrolling');
            this.previousTouches = null;
            this.lastOpenTime = Date.now();
            setTimeout(() => this.classList.add('pdp_zoom-image--active'), 200);
            this.dispatchEvent(new CustomEvent(ZoomImageElement.EVENT_OPENED, { bubbles: true }));
        } else {
            this.fixedImageElement.classList.add('pdp_zoom-image__fixed-image--closing');
            this.curtainElement.classList.add('pdp_zoom-image__curtain--closing');
            this.viewportTransform.reset();
            this.endDrag(false);
            this.viewportPhysics.setEnabled(false);
            this.stopTickLoop();
            this.dispatchEvent(new CustomEvent(ZoomImageElement.EVENT_CLOSED, { bubbles: true }));
        }
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'zoom-image': ZoomImageElement;
    }
}
